import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import Input from "@murphy-frontend/web-core/components/Input";
import Textarea from "@murphy-frontend/web-core/components/TextArea";
import { FaTrashAlt } from "react-icons/fa";

interface ResponseAlternative {
  id: string;
  value: string;
}

interface MBSMessageEditorProps {
  message: string;
  title: string;
  question?: string;
  showQuestionEditor: boolean;
  handleQuestionChange: any;
  handleMessageBodyChange: any;
  handleMessageTitleChange: any;
  messageQuestionError: string;
  messageBodyError: string;
  messageTitleError: string;
  placeholdertext?: string;
  rows: number;
  cols: number;
  showNoOfSms: boolean;
  translations: Record<string, string>;
  responseAlternatives?: ResponseAlternative[];
  onAddResponseAlternative?: () => void;
  onRemoveResponseAlternative?: (id: string) => void;
  onUpdateResponseAlternative?: (id: string, value: string) => void;
  hideOptions: boolean;
}

const styles = {
  row: {
    display: 'flex',
    width: '80rem'
  },
  input: {
    borderWidth: 1,
    borderColor: '#EBEBEB',
    borderStyle: 'solid',
    borderRadius: 5,
    width: '60rem'
  },
  label: {
    width: '10rem',
    color: '#626262'
  },
  required: {
    width: '10rem',
    color: '#626262',
    fontSize: 10,
    fontStyle: 'italic'
  },
  responseAlternativeRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
    width: '60rem'
  },
  responseAlternativeInput: {
    borderWidth: 1,
    borderColor: '#EBEBEB',
    borderStyle: 'solid',
    borderRadius: 5,
    width: '50rem',
    marginRight: '10px'
  },
  addButton: {
    backgroundColor: '#00BCD4',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  removeButton: {
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    width: '30px',
    height: '30px',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}

const MBSMessageEditor: FunctionComponent<MBSMessageEditorProps> = ({
  message,
  title,
  question,
  showQuestionEditor,
  handleQuestionChange,
  handleMessageBodyChange,
  handleMessageTitleChange,
  messageQuestionError,
  messageBodyError,
  messageTitleError,
  placeholdertext,
  rows,
  cols,
  showNoOfSms,
  translations,
  responseAlternatives = [],
  onAddResponseAlternative,
  onRemoveResponseAlternative,
  onUpdateResponseAlternative,
  hideOptions,
}) => {
  const messageInfoMessages = [];

  if (message) {
    const charsPrinted = message.length;
    const charsPerSMS = 160;
    const noOfSms = Math.ceil(charsPrinted / charsPerSMS);
    messageInfoMessages.push(
      `${translations.numberofcharacters}: ${charsPrinted}`
    );
    if (showNoOfSms) {
      messageInfoMessages.push(`${translations.numberof} SMS:  ${noOfSms}`);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 20, paddingRight: 20, marginTop: 20 }}>
      <label style={{ fontSize: '18px', marginBottom: 20, color: 'grey', marginTop: 20, display: 'flex' }}>
        {translations.composemsg || "Compose your message"}
      </label>

      <div style={styles.row}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={styles.label}>{translations.title}</span>
          <span style={styles.required}>{translations.required || "required"}</span>
        </div>
        <Input
          val={title}
          isRequired
          style={styles.input}
          handleChange={handleMessageTitleChange}
          errorMessage={messageTitleError}
          placeholder={translations.thistextwillnotshowinsms}
        />
      </div>

      <div style={styles.row}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={styles.label}>{translations.message}</span>
          <span style={styles.required}>{translations.required || "required"}</span>
        </div>
        <Textarea
          title={""}
          val={message}
          style={styles.input}
          isRequired
          handleChange={handleMessageBodyChange}
          errorMessage={messageBodyError}
          infoMessages={messageInfoMessages}
          rows={rows}
          cols={cols}
          placeholder={placeholdertext}
        />
      </div>

      {showQuestionEditor && (
        <>
          <div style={styles.row}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={styles.label}>{translations.question}</span>
              <span style={styles.required}>{translations.required || "required"}</span>
            </div>
            <Textarea
              title={""}
              val={question}
              style={styles.input}
              isRequired
              handleChange={handleQuestionChange}
              errorMessage={messageQuestionError}
              rows={rows}
              cols={cols}
              placeholder={placeholdertext}
            />
          </div>
          {!hideOptions && (
            <div style={styles.row}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={styles.label}>
                  {translations.answeralternatives || "Response alternatives"}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', width: '60rem' }}>
                {responseAlternatives.map((alternative, index) => (
                  <div key={alternative.id} style={styles.responseAlternativeRow}>
                    <Input
                      val={alternative.value}
                      style={styles.responseAlternativeInput}
                      handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onUpdateResponseAlternative?.(alternative.id, e.target.value)
                      }
                      placeholder={`${translations.alternative || "Alternative"} ${index + 1}`}
                    />
                    <button
                      style={styles.removeButton}
                      onClick={() => onRemoveResponseAlternative?.(alternative.id)}
                      type="button"
                      title={translations.removeAlternative || "Remove alternative"}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                ))}

                <div style={{ marginTop: 10 }}>
                  <button
                    style={styles.addButton}
                    onClick={onAddResponseAlternative}
                    type="button"
                    title={translations.addAlternative || "Add alternative"}
                    aria-label={translations.addAlternative || "Add alternative"}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

MBSMessageEditor.propTypes = {
  message: PropTypes.string,
  title: PropTypes.string,
  question: PropTypes.string,
  messageTitleError: PropTypes.string,
  messageBodyError: PropTypes.string,
  messageQuestionError: PropTypes.string,
  showQuestionEditor: PropTypes.bool,
  placeholdertext: PropTypes.string,
  rows: PropTypes.number.isRequired,
  cols: PropTypes.number.isRequired,
  handleMessageBodyChange: PropTypes.func.isRequired,
  handleMessageTitleChange: PropTypes.func.isRequired,
  handleQuestionChange: PropTypes.func,
  showNoOfSms: PropTypes.bool,
  responseAlternatives: PropTypes.array,
  onAddResponseAlternative: PropTypes.func,
  onRemoveResponseAlternative: PropTypes.func,
  onUpdateResponseAlternative: PropTypes.func,
};

MBSMessageEditor.defaultProps = {
  title: '',
  message: '',
  question: '',
  placeholdertext: '',
  messageTitleError: '',
  messageBodyError: '',
  messageQuestionError: '',
  showQuestionEditor: false,
  handleQuestionChange: null,
  showNoOfSms: false,
  responseAlternatives: [],
  onAddResponseAlternative: null,
  onRemoveResponseAlternative: null,
  onUpdateResponseAlternative: null,
};

export default MBSMessageEditor;